import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import { CloudUpload, CheckCircle, EventBusy, Close } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { toast } from "react-toastify";

const MarkAttendanceModal = ({
  open,
  onClose,
  onSubmit,
  initialData = {},
  loading = false,
  classId,
}) => {
  const [formType, setFormType] = useState("attendance"); // "attendance" or "leave"
  const [attendanceList, setAttendanceList] = useState([]);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState("");
  const [leaveFor, setLeaveFor] = useState("TUTOR"); // "TUTOR" or "PARENT"
  const [selectedLeaveDates, setSelectedLeaveDates] = useState([]); // Array for multiple dates
  const [newLeaveDate, setNewLeaveDate] = useState(""); // Temporary state for adding new date

  const [formData, setFormData] = useState({
    attendanceTime: initialData.attendanceTime || new Date().toISOString(),
    topicCovered: initialData.topicCovered || "",
    homeWork: initialData.homeWork || null,
    voiceNote: initialData.voiceNote || null,
    leaveReason: "",
  });

  // Fetch all attendance records for this class
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!classId || !open) return;

      try {
        const api = (await import("utils/api")).default;
        const response = await api.get("/attendance", {
          params: { classSessionId: classId },
        });

        if (response.data.success) {
          const attendances = response.data.data || [];
          // Show ALL attendance records (both marked and unmarked)
          console.log("=== Attendance Data Debug ===");
          console.log("Total records:", attendances.length);
          console.log("First record:", attendances[0]);
          if (attendances[0]) {
            console.log("First record attendanceTime:", attendances[0].attendanceTime);
            console.log("First record type:", attendances[0].type);
            console.log("First record id:", attendances[0]._id || attendances[0].id);
          }
          console.log("============================");
          setAttendanceList(attendances);
        }
      } catch (error) {
        console.error("Error fetching attendance:", error);
      }
    };

    fetchAttendance();
  }, [classId, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setFormData((prev) => ({ ...prev, [field]: file }));
    }
  };

  const handleSubmit = () => {
    if (formType === "attendance") {
      // Attendance validation
      if (!selectedAttendanceId) {
        toast.error("Please select a date");
        return;
      }

      if (!formData.topicCovered.trim()) {
        toast.error("Please enter topics covered");
        return;
      }

      // Get selected attendance details
      const selectedAttendance = attendanceList.find(
        (att) => att._id === selectedAttendanceId || att.id === selectedAttendanceId
      );

      console.log("=== Submit Data Debug ===");
      console.log("Selected attendance:", selectedAttendance);
      console.log("Form type:", formType);
      console.log("Form data:", formData);

      const submitData = {
        ...formData,
        attendanceId: selectedAttendanceId,
        attendanceTime: selectedAttendance?.attendanceTime || formData.attendanceTime,
        isLeave: false,
      };
      console.log("Attendance submit data:", submitData);
      console.log("=========================");
      onSubmit(submitData);
    } else {
      // Leave validation
      if (selectedLeaveDates.length === 0) {
        toast.error("Please select at least one date");
        return;
      }

      if (!leaveFor) {
        toast.error("Please select Leave For");
        return;
      }

      console.log("=== Submit Leave Data Debug ===");
      console.log("Selected dates:", selectedLeaveDates);
      console.log("Leave For:", leaveFor);
      console.log("Class ID:", classId);

      // Convert dates to ISO format
      const formattedDates = selectedLeaveDates.map((date) => date + "T00:00:00.000Z");

      // For leave, use different payload structure
      const leaveData = {
        classSessionId: classId,
        dates: formattedDates,
        leaveFor: leaveFor,
        isLeave: true,
      };
      console.log("Leave submit data:", leaveData);
      console.log("=========================");
      onSubmit(leaveData);
    }
  };

  const handleClose = () => {
    setFormData({
      attendanceTime: new Date().toISOString(),
      topicCovered: "",
      homeWork: null,
      voiceNote: null,
      leaveReason: "",
    });
    setSelectedAttendanceId("");
    setSelectedLeaveDates([]);
    setNewLeaveDate("");
    setFormType("attendance");
    setLeaveFor("TUTOR");
    onClose();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "No Date";

    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Format type for display
  const formatType = (type) => {
    if (!type) return "REGULAR";
    if (Array.isArray(type)) {
      return type.join(", ");
    }
    return type;
  };

  // Get display text for dropdown item
  const getAttendanceDisplayText = (attendance) => {
    const dateStr = formatDate(attendance.attendanceTime);
    const typeStr = formatType(attendance.type);
    const isMarked = attendance.topicCovered && attendance.topicCovered.trim() !== "";
    const status = isMarked ? " ✓ (Marked)" : " (Not Marked)";
    return `${dateStr} - ${typeStr}${status}`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 2.5,
          position: "relative",
        }}
      >
        <ArgonBox>
          <ArgonTypography variant="h5" color="white" fontWeight="bold">
            Mark Attendance / Leave
          </ArgonTypography>
          <ArgonTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
            Select a date and mark attendance or leave for the class
          </ArgonTypography>
        </ArgonBox>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <ArgonBox display="flex" flexDirection="column" gap={3}>
          {/* Form Type Selection */}
          <ArgonBox>
            <FormControl component="fieldset">
              <FormLabel
                component="legend"
                sx={{
                  fontSize: "14px",
                  fontWeight: "medium",
                  color: "#344767",
                  mb: 1,
                }}
              >
                Select Type
              </FormLabel>
              <RadioGroup
                row
                value={formType}
                onChange={(e) => {
                  setFormType(e.target.value);
                  setFormData((prev) => ({ ...prev, topicCovered: "", leaveReason: "" }));
                }}
                sx={{ gap: 2, ml: 1.4 }}
              >
                <FormControlLabel
                  value="attendance"
                  control={
                    <Radio
                      color="success"
                      sx={{
                        "&.Mui-checked": {
                          color: "#4CAF50",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                        },
                      }}
                    />
                  }
                  label={
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <CheckCircle sx={{ fontSize: 18, color: "#4CAF50" }} />
                      <ArgonTypography variant="button" fontWeight="medium">
                        MARK ATTENDANCE
                      </ArgonTypography>
                    </ArgonBox>
                  }
                  sx={{
                    border: formType === "attendance" ? "2px solid #4CAF50" : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    mr: 0,
                    backgroundColor: formType === "attendance" ? "#f1f8f4" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                />
                <FormControlLabel
                  value="leave"
                  control={
                    <Radio
                      color="error"
                      sx={{
                        "&.Mui-checked": {
                          color: "#f44336",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                        },
                      }}
                    />
                  }
                  label={
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <EventBusy sx={{ fontSize: 18, color: "#f44336" }} />
                      <ArgonTypography variant="button" fontWeight="medium">
                        MARK LEAVE
                      </ArgonTypography>
                    </ArgonBox>
                  }
                  sx={{
                    border: formType === "leave" ? "2px solid #f44336" : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    backgroundColor: formType === "leave" ? "#fef5f5" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                />
              </RadioGroup>
            </FormControl>
          </ArgonBox>

          {/* Date Selection - Different UI for Attendance vs Leave */}
          {formType === "attendance" ? (
            /* Dropdown for Attendance - Select from existing dates */
            <ArgonBox>
              <ArgonTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ mb: 1, display: "block" }}
              >
                Select Date *
              </ArgonTypography>
              <FormControl fullWidth>
                <Select
                  value={selectedAttendanceId}
                  onChange={(e) => setSelectedAttendanceId(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 45,
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d2d6da",
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Choose a date to mark
                  </MenuItem>
                  {attendanceList.length === 0 ? (
                    <MenuItem value="" disabled>
                      No attendance records found
                    </MenuItem>
                  ) : (
                    attendanceList.map((attendance) => {
                      const displayText = getAttendanceDisplayText(attendance);
                      return (
                        <MenuItem
                          key={attendance._id || attendance.id}
                          value={attendance._id || attendance.id}
                        >
                          {displayText}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>
            </ArgonBox>
          ) : (
            /* Calendar Date Picker for Leave - Multiple dates */
            <ArgonBox>
              <ArgonTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ mb: 1, display: "block" }}
              >
                Select Leave Dates *
              </ArgonTypography>

              {/* Date Input with Add Button */}
              <ArgonBox display="flex" gap={1} mb={2} alignItems="center">
                <TextField
                  fullWidth
                  type="date"
                  value={newLeaveDate}
                  onChange={(e) => setNewLeaveDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 45,
                      borderRadius: "8px",
                    },
                  }}
                />
                <ArgonButton
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    if (newLeaveDate && !selectedLeaveDates.includes(newLeaveDate)) {
                      setSelectedLeaveDates([...selectedLeaveDates, newLeaveDate]);
                      setNewLeaveDate("");
                    } else if (selectedLeaveDates.includes(newLeaveDate)) {
                      toast.warning("Date already added");
                    }
                  }}
                  sx={{
                    minWidth: "120px",
                    height: "45px",
                    flexShrink: 0,
                  }}
                >
                  Add Date
                </ArgonButton>
              </ArgonBox>

              {/* Selected Dates List */}
              {selectedLeaveDates.length > 0 && (
                <ArgonBox
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    p: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={1}>
                    Selected Dates ({selectedLeaveDates.length}):
                  </ArgonTypography>
                  <ArgonBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {selectedLeaveDates.map((date, index) => (
                      <ArgonBox
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          backgroundColor: "white",
                          border: "1px solid #5e72e4",
                          borderRadius: "8px",
                          px: 2,
                          py: 1,
                        }}
                      >
                        <ArgonTypography variant="caption" fontWeight="medium">
                          {new Date(date).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </ArgonTypography>
                        <ArgonButton
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => {
                            setSelectedLeaveDates(selectedLeaveDates.filter((d) => d !== date));
                          }}
                          sx={{ minWidth: "auto", p: 0, fontSize: "18px" }}
                        >
                          ×
                        </ArgonButton>
                      </ArgonBox>
                    ))}
                  </ArgonBox>
                </ArgonBox>
              )}
            </ArgonBox>
          )}

          {/* Conditional Fields Based on Form Type */}
          {formType === "attendance" ? (
            <>
              {/* Topic Covered */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Topics Covered *
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter topics discussed in this session..."
                  value={formData.topicCovered}
                  onChange={(e) => handleChange("topicCovered", e.target.value)}
                />
              </ArgonBox>

              {/* File Uploads */}
              <Grid container spacing={2}>
                {/* Homework Upload */}
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Homework (Optional)
                    </ArgonTypography>
                    <ArgonBox
                      component="label"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #d2d6da",
                        borderRadius: 2,
                        p: 2.5,
                        cursor: "pointer",
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "#5e72e4",
                          background: "#f8f9fe",
                        },
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                      <ArgonTypography variant="caption" color="text" textAlign="center">
                        {formData.homeWork ? formData.homeWork.name : "Upload PDF/Image"}
                      </ArgonTypography>
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange("homeWork", e.target.files[0])}
                      />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Voice Note Upload */}
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Voice Note (Optional)
                    </ArgonTypography>
                    <ArgonBox
                      component="label"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #d2d6da",
                        borderRadius: 2,
                        p: 2.5,
                        cursor: "pointer",
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "#5e72e4",
                          background: "#f8f9fe",
                        },
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                      <ArgonTypography variant="caption" color="text" textAlign="center">
                        {formData.voiceNote ? formData.voiceNote.name : "Upload Audio"}
                      </ArgonTypography>
                      <input
                        type="file"
                        hidden
                        accept="audio/*"
                        onChange={(e) => handleFileChange("voiceNote", e.target.files[0])}
                      />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>
            </>
          ) : (
            /* Leave Section */
            <>
              {/* Leave For Dropdown */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Leave For *
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={leaveFor}
                  options={[
                    { value: "TUTOR", label: "Tutor" },
                    { value: "PARENT", label: "Parent" },
                  ]}
                  onChange={(e) => setLeaveFor(e.target.value)}
                  width="100%"
                />
              </ArgonBox>
            </>
          )}
        </ArgonBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <ArgonButton
          onClick={handleClose}
          variant="outlined"
          color="secondary"
          disabled={loading}
          fullWidth
        >
          Cancel
        </ArgonButton>
        <ArgonButton
          variant="gradient"
          color={formType === "attendance" ? "success" : "error"}
          onClick={handleSubmit}
          disabled={
            loading ||
            (formType === "attendance" ? !selectedAttendanceId : selectedLeaveDates.length === 0)
          }
          fullWidth
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : formType === "attendance" ? (
            "Submit Attendance"
          ) : (
            "Submit Leave"
          )}
        </ArgonButton>
      </DialogActions>
    </Dialog>
  );
};

MarkAttendanceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  loading: PropTypes.bool,
  classId: PropTypes.string,
};

export default MarkAttendanceModal;
